/* Keep details open until the whole panel has finished fading out. */
(() => {
  if (customElements.get('orn-filter-disclosure')) return;
  customElements.define('orn-filter-disclosure', class extends HTMLElement {
    connectedCallback() {
      this.details = this.querySelector('details');
      this.summary = this.details?.querySelector('summary');
      if (!this.summary) return;
      this.controller = new AbortController();
      this.desiredOpen = this.details.open;
      this.summary.setAttribute('aria-expanded', String(this.desiredOpen));
      this.summary.addEventListener('click', event => {
        event.preventDefault();
        this.setOpen(!this.desiredOpen);
      }, { signal: this.controller.signal });
      this.details.addEventListener('toggle', () => {
        // The mobile drawer resets native details when it closes.
        if (!this.details.open && this.desiredOpen) this.finish(false);
      }, { signal: this.controller.signal });
    }
    setOpen(open) {
      const start = this.details.getBoundingClientRect().height;
      this.animation?.cancel();
      this.desiredOpen = open;
      this.summary.setAttribute('aria-expanded', String(open));
      this.details.open = true;
      const border = parseFloat(getComputedStyle(this.details).borderTopWidth) || 0;
      const end = open ? this.details.getBoundingClientRect().height : this.summary.getBoundingClientRect().height + border;
      if (matchMedia('(prefers-reduced-motion: reduce)').matches) return this.finish(open);
      this.details.style.overflow = 'hidden';
      const animation = this.details.animate([{ height: `${start}px` }, { height: `${end}px` }], {
        duration: 280, easing: 'cubic-bezier(.25,.1,.25,1)', fill: 'both'
      });
      this.animation = animation;
      animation.finished.then(() => {
        if (this.animation === animation) this.finish(open);
      }).catch(() => {});
    }
    finish(open) {
      this.animation?.cancel();
      this.animation = null;
      this.desiredOpen = open;
      this.details.open = open;
      this.details.style.overflow = '';
      this.summary.setAttribute('aria-expanded', String(open));
    }
    disconnectedCallback() { this.animation?.cancel(); this.controller?.abort(); }
  });
})();

/* Share one unfiltered count request between the desktop and mobile menus. */
(() => {
  if (customElements.get('orn-family-counts')) return;
  const requests = new Map();
  customElements.define('orn-family-counts', class extends HTMLElement {
    async connectedCallback() {
      const url = this.dataset.url;
      if (!url) return;
      try {
        if (!requests.has(url)) {
          requests.set(url, fetch(url, { credentials: 'same-origin' }).then(response => {
            if (!response.ok) throw new Error('Menu counts unavailable');
            return response.text();
          }).then(html => {
            const document = new DOMParser().parseFromString(html, 'text/html');
            const rows = Array.from(document.querySelectorAll('[data-param][data-value][data-count]'));
            if (!rows.length) throw new Error('No menu counts returned');
            return rows.map(row => ({ param: row.dataset.param, value: row.dataset.value, count: row.dataset.count }));
          }).catch(error => { requests.delete(url); throw error; }));
        }
        const counts = await requests.get(url);
        if (!this.isConnected) return;
        this.querySelectorAll('a').forEach(link => {
          const params = new URL(link.href, location.origin).searchParams;
          const count = counts.find(row => params.getAll(row.param).includes(row.value));
          const target = link.querySelector('[data-family-count]');
          if (count && target && /^\d+$/.test(count.count)) {
            target.textContent = count.count;
            target.hidden = false;
          }
        });
      } catch (_) { /* Keep navigation usable when the count request cannot complete. */ }
    }
  });
})();

(() => {
  if (customElements.get('orn-menu-disclosure')) return;
  customElements.define('orn-menu-disclosure', class extends HTMLElement {
    connectedCallback() {
      this.details = this.querySelector('details');
      const summary = this.details?.querySelector(':scope > summary');
      this.trigger = summary?.querySelector('a') || summary;
      this.panel = this.details?.querySelector('.orn-mega');
      if (!this.trigger || !this.panel) return;
      this.controller = new AbortController();
      this.desiredOpen = false;
      this.details.open = false;
      this.panel.style.opacity = '0';
      this.trigger.setAttribute('aria-expanded', 'false');
      const on = (target, name, callback) => target.addEventListener(name, callback, { signal: this.controller.signal });
      const hover = () => matchMedia('(hover: hover) and (pointer: fine)').matches;
      const cancel = () => clearTimeout(this.closeTimer);
      const closeSoon = () => {
        cancel();
        this.closeTimer = setTimeout(() => {
          if (!this.panel.contains(document.activeElement)) this.setOpen(false);
        }, 120);
      };
      on(this.trigger, 'click', event => {
        // A real link keeps normal navigation, including modifier-click and new tabs.
        if (this.trigger.matches('a[href]')) return;
        event.preventDefault();
        this.setOpen(!this.desiredOpen);
      });
      on(this.trigger, 'pointerenter', () => { if (hover()) this.setOpen(true); });
      on(this.trigger, 'pointerleave', () => { if (hover()) closeSoon(); });
      on(this.panel, 'pointerenter', () => { cancel(); if (hover()) this.setOpen(true); });
      on(this.panel, 'pointerleave', () => { if (hover()) closeSoon(); });
      // Keep desktop wheel/trackpad gestures from moving the menu or the page behind it.
      // Ctrl-wheel is browser zoom and must retain its native behaviour.
      this.panel.addEventListener('wheel', event => {
        if (this.desiredOpen && !event.ctrlKey) event.preventDefault();
      }, { passive: false, signal: this.controller.signal });
      on(this, 'keydown', event => {
        if (event.key === 'ArrowDown' && event.target === this.trigger) {
          event.preventDefault();
          this.setOpen(true);
          this.panel.querySelector('a')?.focus();
        }
      });
      on(document, 'keydown', event => {
        if (event.key === 'Escape' && this.desiredOpen) {
          event.preventDefault();
          this.setOpen(false);
          this.trigger.focus();
        }
      });
      on(document, 'pointerdown', event => { if (!this.contains(event.target)) this.setOpen(false); });
      on(document, 'focusin', event => { if (!this.contains(event.target)) this.setOpen(false); });
      on(this, 'focusout', event => { if (!event.relatedTarget) this.setOpen(false); });
      on(window, 'resize', () => {
        if (!matchMedia('(min-width: 1000px)').matches) this.setOpen(false);
        else if (this.details.open) this.measureTop();
      });
      on(window, 'pagehide', () => this.setOpen(false));
    }
    measureTop() {
      this.panel.style.setProperty('--orn-menu-top', `${Math.max(0, this.panel.getBoundingClientRect().top)}px`);
    }
    setOpen(open) {
      clearTimeout(this.closeTimer);
      if (open === this.desiredOpen) return;
      const wasVisible = this.details.open;
      const opacity = wasVisible ? getComputedStyle(this.panel).opacity : '0';
      this.desiredOpen = open;
      this.animation?.cancel();
      this.trigger.setAttribute('aria-expanded', String(open));
      this.panel.inert = !open;
      if (open) {
        this.details.open = true;
        this.measureTop();
        // A new selection is made once per opening, never while reading the menu.
        this.details.dispatchEvent(new CustomEvent('orn:menu-open'));
      }
      const target = open ? '1' : '0';
      if (matchMedia('(prefers-reduced-motion: reduce)').matches) {
        this.panel.style.opacity = target;
        this.details.open = open;
        return;
      }
      const animation = this.panel.animate([{ opacity }, { opacity: target }], {
        duration: open ? 240 : 200, easing: 'ease-in-out', fill: 'both'
      });
      this.animation = animation;
      animation.finished.then(() => {
        if (this.animation !== animation) return;
        this.panel.style.opacity = target;
        if (!this.desiredOpen) this.details.open = false;
        animation.cancel();
        this.animation = null;
      }).catch(() => {}); // Re-entering mid-fade reverses from the current opacity.
    }
    disconnectedCallback() {
      clearTimeout(this.closeTimer);
      this.animation?.cancel();
      this.controller?.abort();
    }
  });
})();

/* Editor-selected products and atmosphere images are rendered with the menu. */
(() => {
  if (customElements.get('orn-featured-fragrance')) return;
  customElements.define('orn-featured-fragrance', class extends HTMLElement {
    connectedCallback() {
      this.controller = new AbortController();
      this.slides = Array.from(this.querySelectorAll('[data-orn-slide]'));
      this.index = -1;
      const on = (target, type, handler) => target?.addEventListener(type, handler, { signal: this.controller.signal });
      on(this.closest('details'), 'orn:menu-open', () => this.chooseRandom());
      const mobilePanel = this.closest('.orn-mobile-panel');
      if (mobilePanel) {
        // The native drawer unhides this panel on every entry, including reopening it.
        this.panelObserver = new MutationObserver(records => {
          if (records.some(record => record.attributeName === 'hidden') && !mobilePanel.hidden) {
            this.chooseRandom();
            mobilePanel.closest('.header-sidebar__scroller')?.scrollTo({ top: 0 });
          }
        });
        this.panelObserver.observe(mobilePanel, { attributes: true, attributeFilter: ['hidden'] });
      }
      on(this, 'shopify:block:select', event => {
        const index = this.slides.findIndex(slide => slide.dataset.blockId === event.detail?.blockId);
        if (index < 0) return;
        this.closest('orn-menu-disclosure')?.setOpen(true);
        this.select(index);
      });
      this.chooseRandom();
    }
    chooseRandom() {
      const count = this.slides.length;
      if (!count) return;
      // Give each distinct fragrance equal odds, independent of the editor's block order.
      // Exclude the last fragrance so consecutive openings never repeat it.
      const previousProduct = this.slides[this.index]?.dataset.productId;
      const products = new Map();
      this.slides.forEach((slide, index) => {
        const product = slide.dataset.productId;
        if (product === previousProduct) return;
        if (!products.has(product)) products.set(product, []);
        products.get(product).push(index);
      });
      const candidates = Array.from(products.values());
      if (!candidates.length) return;
      const images = candidates[Math.floor(Math.random() * candidates.length)];
      this.select(images[Math.floor(Math.random() * images.length)]);
    }
    select(index) {
      this.index = index;
      this.slides.forEach((slide, i) => {
        const active = i === index;
        slide.classList.toggle('is-active', active);
        slide.inert = !active;
        slide.setAttribute('aria-hidden', String(!active));
        if (active) {
          const img = slide.querySelector('img');
          if (img) img.loading = 'eager';
        }
      });
    }
    disconnectedCallback() { this.panelObserver?.disconnect(); this.controller?.abort(); }
  });
})();
